import * as React from 'react';
import { Link, useLocation } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { carried, replacesEntry } from './carried.ts';
import './rail.css';

// The navigation rail: a column of icons, each with its title under it, on
// the frame's left edge, present on every screen, so no screen needs a way
// in or out of its own. The frame declares `shell.rail` and draws nothing
// there by itself; this plugin fills it and declares `rail.entries`, which
// screens contribute to with an icon, a title, a route and an order. Entries
// marked `end` sit at the bottom. Entries sharing a `section` form a group,
// set apart from its neighbours by a divider. An entry may carry address
// parameters over (`keep`), as the dashboard screens do with `?game`.
export interface RailEntryParams {
  id: string;
  title: string;
  to: string;
  /** Phosphor icon path data, viewBox 0 0 256 256. */
  icon: string;
  order?: number;
  end?: boolean;
  /** Entries with the same section sit together between dividers, e.g. 'stages'. */
  section?: string;
  /**
   * Address parameters carried over from the current address when the link
   * is followed, e.g. `['game']`: the dashboard screens keep the selected
   * game when moving between them.
   */
  keep?: string[];
}

type Extension = PiletApi['Extension'];

const byOrder = <T extends { defaults?: { order?: number } }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.defaults?.order ?? 0) - (b.defaults?.order ?? 0));

function isActive(path: string, to: string): boolean {
  return to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
}

const Rail: React.FC<{ Extension: Extension }> = ({ Extension }) => {
  const { pathname, search } = useLocation();
  return (
    <Extension
      name="rail.entries"
      order={byOrder}
      render={(children: React.ReactNode) => {
        const all = (React.Children.toArray(children) as React.ReactElement[])
          .map((el) => el.props?.params as RailEntryParams | undefined)
          .filter((p): p is RailEntryParams => !!p && typeof p.to === 'string');
        // One entry per id, the last registered winning: a screen plugin
        // re-registered by a hot reload must not appear twice.
        const byId = new Map<string, RailEntryParams>();
        for (const e of all) byId.set(e.id, e);
        const entries = [...byId.values()];
        const link = (e: RailEntryParams) => {
          const active = isActive(pathname, e.to);
          return (
            <Link
              key={e.id}
              to={carried(e.to, search, e.keep)}
              replace={replacesEntry(pathname, e.to)}
              className={`rail-link${active ? ' rail-link-active' : ''}`}
              aria-current={active ? 'page' : undefined}>
              <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
                <path d={e.icon} />
              </svg>
              <span className="rail-label">{e.title}</span>
            </Link>
          );
        };
        // A divider wherever the section changes between neighbours: an
        // entry without a section is a group of its own.
        const top = entries.filter((e) => !e.end);
        const withDividers: React.ReactNode[] = [];
        top.forEach((e, i) => {
          const prev = top[i - 1];
          if (prev && (e.section ?? e.id) !== (prev.section ?? prev.id) && (e.section || prev.section)) {
            withDividers.push(<hr key={`divider-${e.id}`} className="rail-divider" aria-hidden="true" />);
          }
          withDividers.push(link(e));
        });
        return (
          <nav className="rail" aria-label="Main">
            {withDividers}
            <div className="rail-spacer" />
            {entries.filter((e) => e.end).map(link)}
          </nav>
        );
      }}
    />
  );
};

export function setup(app: PiletApi) {
  app.registerExtension('shell.rail', () => <Rail Extension={app.Extension} />);
}
